import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Los esqueletos de PÁGINA, en un solo sitio (29-08-2026).
 *
 * `SheetBodySkeleton` cubre el cuerpo de una HOJA —tarjeta arriba y filas
 * debajo— y con eso se resolvieron las fichas de detalle. Lo que faltaba era el
 * otro lado: lo que se enseña mientras llega una PÁGINA entera. Ahí no había
 * esqueleto ninguno, había un anillo girando (`PageLoader`), y era el mismo para
 * el mapa, para la lista del equipo y para un informe.
 *
 * LA REGLA, la misma que en la hoja: reservar el hueco con la FORMA de lo que va
 * a caer encima. Un esqueleto que no se parece a su contenido no evita el salto,
 * solo lo adorna; y uno más alto que su contenido lo empeora, porque al llegar
 * los datos la página se encoge.
 *
 * NADA CARO AQUÍ DENTRO: solo `animate-pulse`, que es una opacidad y la resuelve
 * el compositor. Ni sombras, ni desenfoques, ni degradados. Esto se pinta
 * mientras una página está entrando —y en el teléfono, mientras además se está
 * descargando su trozo de JavaScript—, así que tiene que costar lo mínimo.
 *
 * Las medidas salen de las pantallas reales, no de la imaginación: `h-16
 * rounded-2xl` es la fila del listado de equipo y de tareas, `h-28
 * rounded-[18px]` la tarjeta de la lista de sesiones, `h-[168px]` el bloque de
 * gráfica del planificador, y las baldosas de 76 px son las de los indicadores
 * de Analíticas.
 */

/** Una barra de título más un subtítulo: la cabecera que abre casi toda página. */
export function SkeletonPageHeader({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn("space-y-2", className)}>
      <Skeleton className="h-7 w-48 rounded-xl" />
      <Skeleton className="h-4 w-64 rounded-lg" />
    </div>
  );
}

/** Filas de una lista. La forma de Equipo, Tareas, Monitoreo, Registros. */
export function SkeletonList({
  rows = 4,
  height = 64,
  className,
}: {
  rows?: number;
  /** Alto de cada fila en píxeles. 64 es la fila estándar de esta app. */
  height?: number;
  className?: string;
}) {
  const count = Math.max(0, Math.floor(rows));
  return (
    <div aria-hidden="true" className={cn("space-y-2.5", className)}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="w-full rounded-2xl" style={{ height }} />
      ))}
    </div>
  );
}

/** Rejilla de tarjetas: ubicaciones, invernaderos, productos. */
export function SkeletonCards({
  count = 6,
  height = 132,
  className,
}: {
  count?: number;
  height?: number;
  className?: string;
}) {
  const n = Math.max(0, Math.floor(count));
  return (
    <div
      aria-hidden="true"
      className={cn("grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", className)}
    >
      {Array.from({ length: n }, (_, i) => (
        <Skeleton key={i} className="w-full rounded-2xl" style={{ height }} />
      ))}
    </div>
  );
}

/** Una fila de indicadores: las baldosas cuadradas de Analíticas e informes. */
export function SkeletonKpiRow({
  cols = 4,
  tile = 76,
  className,
}: {
  cols?: 2 | 3 | 4;
  tile?: number;
  className?: string;
}) {
  const columnas = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2 md:grid-cols-4" }[cols];
  return (
    <div aria-hidden="true" className={cn("grid gap-2", columnas, className)}>
      {Array.from({ length: cols }, (_, i) => (
        <Skeleton key={i} className="w-full rounded-2xl" style={{ height: tile }} />
      ))}
    </div>
  );
}

/** El hueco de una gráfica. */
export function SkeletonChart({ height = 168, className }: { height?: number; className?: string }) {
  return (
    <Skeleton aria-hidden="true" className={cn("w-full rounded-2xl", className)} style={{ height }} />
  );
}

export type PageSkeletonVariant = "list" | "cards" | "report" | "map";

/**
 * La página entera mientras llega.
 *
 * `variant` no es decoración: es lo que hace que el hueco se parezca a la
 * pantalla que se ha pedido. Quien la use debe elegir la que corresponda a SU
 * ruta; `list` es el valor por defecto porque es la forma más común de esta app.
 */
export function PageSkeleton({
  variant = "list",
  className,
}: {
  variant?: PageSkeletonVariant;
  className?: string;
}) {
  return (
    <div className={cn("w-full space-y-5 py-2", className)}>
      <SkeletonPageHeader />
      {variant === "list" && <SkeletonList rows={5} />}
      {variant === "cards" && <SkeletonCards count={6} />}
      {variant === "report" && (
        <div className="space-y-4">
          <SkeletonKpiRow cols={4} />
          <SkeletonChart height={220} />
          <SkeletonList rows={3} height={56} />
        </div>
      )}
      {/* El mapa es una sola superficie: una lámina, no una lista. */}
      {variant === "map" && <Skeleton aria-hidden="true" className="h-[52vh] w-full rounded-2xl" />}
    </div>
  );
}

/**
 * Qué forma tiene la ruta a la que se está yendo.
 *
 * Va aquí y no en cada armazón para que los tres —gestor, operario y perito—
 * digan lo mismo. Es una tabla de presentación, nada más: si una ruta no está,
 * sale la forma de lista, que es la más común.
 */
export function pageSkeletonVariantFor(pathname: string): PageSkeletonVariant {
  if (pathname.startsWith("/blocks")) return "map";
  if (pathname.startsWith("/analytics") || pathname.startsWith("/reports")) return "report";
  if (pathname.startsWith("/inventory") || pathname.startsWith("/shop")) return "cards";
  if (pathname.startsWith("/dashboard")) return "cards";
  return "list";
}
